import {
  WSEmptyState,
  WSFilters,
  WSInfiniteScroll
} from "@wingspanhq/fe-component-library";
import { CustomFieldResourceType } from "@wingspanhq/payments/dist/interfaces";
import React, { useMemo } from "react";
import { RouteComponentProps, useHistory } from "react-router-dom";
import { useQueryCustomFieldsAll } from "../../../../query/customFields/queries/useQueryCustomFieldsAll";
import { useQueryEngagementsListAll } from "../../../../query/engagements/queries/useQueryEngagementsListAll";
import { useFeatureFlags } from "../../../../query/hooks/useFeatureFlags";
import { useQueryRequirementDefinitionListAll } from "../../../../query/requirementDefinitions/query/useQueryRequirementDefinitionListAll";
import { useQueryPayeeRows } from "../../../../query/search/payee/queries/useQueryPayeeRows";
import { useQueryPayeeRowsSummary } from "../../../../query/search/payee/queries/useQueryPayeeRowsSummary";
import { useQueryAllOrganizationUsers } from "../../../../query/users/queries";
import { WSQueries } from "../../../../query/WSQuery";
import { PayeeRowsQuery } from "../../../../services/search";
import { useBrowserPageTitle } from "../../../../shared/hooks/useBrowserPageTitle";
import { useUrlQueryFilters } from "../../../../shared/hooks/useUrlQueryFilters";
import { useGoToContractorImportStart } from "../../../BulkImporter/paths";
import { useBulkCollaboratorBatchesQuery } from "../../../BulkImporter/query/bulkCollaborator/queries";
import { useCustomization } from "../../../customization";
import { useCreateContractorModal } from "../../components/CreateContractorModal/useCreateContractorModal";
import { PayeeRowsTable } from "../../components/PayeeRowsTable";
import {
  PAYEES_ARCHIVED_PATH,
  PAYEES_COMPLETE_PATH,
  PAYEES_INCOMPLETE_PATH,
  PAYEES_NOT_SIGNED_UP_PATH
} from "../../utils";
import { AlertBulkReview } from "./AlertBulkReview";
import { buildConfig, getInterceptFiltersChange } from "./filters/config";
import { createListRequest } from "./filters/request";
import { PayeeFilterEligibilityStatus, PayeeFilters } from "./filters/types";
import { ContractorsTabsFilters } from "../RouteDashboard/DashboardTabs";

const mapPathToFilters = (path: string): PayeeRowsQuery => {
  switch (path) {
    case PAYEES_INCOMPLETE_PATH: {
      return ContractorsTabsFilters.incomplete;
    }
    case PAYEES_COMPLETE_PATH: {
      return ContractorsTabsFilters.complete;
    }
    case PAYEES_ARCHIVED_PATH: {
      return ContractorsTabsFilters.archived;
    }
    case PAYEES_NOT_SIGNED_UP_PATH: {
      return ContractorsTabsFilters.notSignedUp;
    }
  }

  return {};
};

export const RoutePayees: React.FC<RouteComponentProps> = ({
  match,
  location
}) => {
  const { terminology } = useCustomization();

  useBrowserPageTitle(terminology().sendPaymentsContractor);
  const history = useHistory();
  const { filters, setFilters } = useUrlQueryFilters<PayeeFilters>();
  const modalCreateContractor = useCreateContractorModal();
  const queryOrgAccounts = useQueryAllOrganizationUsers();

  const queryFeatureFlags = useFeatureFlags();
  const queryCustomFields = useQueryCustomFieldsAll({
    resourceType: [CustomFieldResourceType.Collaborator]
  });

  const tabFilters = useMemo(() => mapPathToFilters(match.path), [match.path]);

  const hasTabFilters = useMemo(
    () => Object.keys(tabFilters).length > 0,
    [tabFilters]
  );

  const request = useMemo(
    () =>
      hasTabFilters
        ? tabFilters
        : createListRequest(
            {
              ...filters
            },
            queryCustomFields.data
          ),
    [filters, queryCustomFields.data, tabFilters, hasTabFilters]
  );

  const queryPayeeRows = useQueryPayeeRows(request);
  const queryPayeeRowsSummary = useQueryPayeeRowsSummary(request);
  const queryAllPayeesRowsSummary = useQueryPayeeRowsSummary(
    ContractorsTabsFilters.all
  );

  const queryEngagementList = useQueryEngagementsListAll({
    filter: {
      isDefault: false
    }
  });
  const queryBulkPayableBatches = useBulkCollaboratorBatchesQuery();
  const goToContractorImportStart = useGoToContractorImportStart();

  const queryRequirements = useQueryRequirementDefinitionListAll();

  const primaryFiltersConfig = useMemo(() => {
    return buildConfig({
      orgAccounts: queryOrgAccounts.data || [],
      engagements: queryEngagementList.data || [],
      bulkBatches: queryBulkPayableBatches.data || [],
      customFields: queryCustomFields.data || [],
      requirements: queryRequirements.data || []
    });
  }, [
    queryBulkPayableBatches.data,
    queryCustomFields.data,
    queryEngagementList.data,
    queryOrgAccounts.data,
    queryRequirements.data
  ]);

  const interceptFiltersChange = useMemo(
    () => getInterceptFiltersChange(queryRequirements.data || []),
    [queryRequirements.data]
  );

  return (
    <>
      {!!queryAllPayeesRowsSummary.data?.listSize && (
        <WSFilters<PayeeFilters>
          my="L"
          searchable
          values={filters}
          onFilter={setFilters}
          interceptChange={interceptFiltersChange}
          primaryFilters={primaryFiltersConfig}
          hidePrimaryFiltersButton={hasTabFilters}
          info={{
            count: queryPayeeRowsSummary.data?.listSize || 0
          }}
        />
      )}

      <WSQueries
        queries={{
          queryPayeeRows
        }}
      >
        {({ queryPayeeRowsData }) =>
          Object.keys(filters).length && queryPayeeRowsData.length === 0 ? (
            <WSEmptyState
              py="3XL"
              mt="L"
              hasBorder
              bgColor="gray"
              type="search"
              title="No results found"
              description="Try adjusting your search or filter to find what you’re looking for."
            />
          ) : queryPayeeRowsData.length === 0 ? (
            <WSEmptyState
              py="3XL"
              mt="L"
              hasBorder
              type="people"
              title="No contractors yet"
              bgColor="gray"
              description={
                hasTabFilters
                  ? undefined
                  : "Once a contractor is added, they will appear here."
              }
              actions={
                hasTabFilters
                  ? undefined
                  : {
                      buttons: [
                        {
                          label: "Add contractor",
                          kind: "Secondary",
                          onClick: () => {
                            modalCreateContractor.open({
                              redirectToDetails: true
                            });
                          }
                        },
                        {
                          label: "Bulk upload contractors",
                          kind: "Tertiary",
                          onClick: async () => {
                            goToContractorImportStart();
                          }
                        }
                      ]
                    }
              }
            />
          ) : (
            <>
              {queryFeatureFlags.data?.requirementsBulkReview &&
                (filters.eligibilityStatus ===
                  PayeeFilterEligibilityStatus.Incomplete ||
                  filters.eligibilityStatus ===
                    PayeeFilterEligibilityStatus.SpecificRequirement) && (
                  <AlertBulkReview payeeFilters={filters} />
                )}
              <WSInfiniteScroll
                mt="M"
                onLoad={() => {
                  queryPayeeRows.fetchNextPage();
                }}
                loadMore={queryPayeeRowsData.length > 0}
                endOfList={!queryPayeeRows.hasNextPage}
                loading={!!queryPayeeRows.isFetchingNextPage}
              >
                <PayeeRowsTable payees={queryPayeeRowsData} filters={request} />
              </WSInfiniteScroll>
            </>
          )
        }
      </WSQueries>
    </>
  );
};
